import axios from '@providers/axiosProvider';
import * as actions from './actions';
import { commonActions } from '@ducks/common';

import {
  APICreateWork,
  APICreateWorkHistory,
  APICreateWorkHistoryRequest,
  APICreateWorkHistoryRequestParams,
  APICreateWorkRequest,
  APICreateWorkRequestParams,
  APICreateWorkResponse,
  APIDeleteWorksRequest,
  APIExportWorkReportResponse,
  APIGetWorkHistoryResponse,
  APIGetWorkResponse,
  APIGetWorksItemDataResponse,
  APIGetWorksRequestParams,
  APIGetWorksAllRequestParams,
  APIGetWorksResponse,
  APIExportCsvFormatResponse,
  APIUpdateWork,
  APIUpdateWorkHistory,
  APIUpdateWorkHistoryRequest,
  APIUpdateWorkHistoryRequestParams,
  APIUpdateWorkRequest,
  APIUpdateWorkRequestParams,
  Work,
  WorkHistory,
  WorkHistorySubmitData,
  WorksItemData,
  WorksList,
  WorksListInfo,
  WorkCreateSubmitData,
  WorkUpdateSubmitDate,
  APIGetWorkInformationResponse,
  WorkInfo,
  APIApproveWorkHistoryRequest,
  APITakeBackWorkHistoryRequest,
  APIGetWorkHistoryRequestParams,
  APIGetTroubleReportPlaceHoldersResponse,
  APIDownloadTroubleReportFormatResponse,
  TroubleReportPlaceholderRelation,
} from './types';

import { LIST_SIZE_PER_PAGE } from '@utils/constants';
import { formatDateString } from '@utils/dateUtils';
import { CustomPropertySearchFromData } from '@ducks/customProperty/types';

/**
 * @summary 案件一覧取得
 * @param {number | null} propertyId 建物ID
 * @param {APIGetWorksRequestParams} params 検索パラメータ(query)
 * @param {CustomPropertySearchFromData} customPropertySearchForm 検索パラメータ(body)
 * @returns {Promise<void>}
 */
export const getWorksAsync =
  (
    propertyId: number | null,
    params: APIGetWorksRequestParams,
    customPropertySearchForm?: CustomPropertySearchFromData,
  ) =>
  async (dispatch: any) => {
    dispatch(actions.setIsLoading(true));

    const reqParams: APIGetWorksRequestParams = {
      ...params,
      size_per_page: LIST_SIZE_PER_PAGE,
    };

    if (propertyId != null) reqParams.property_id = propertyId;

    await axios
      .post<APIGetWorksResponse>(
        '/troubles/list',
        { ...customPropertySearchForm },
        { params: reqParams },
      )
      .then((response) => {
        const { trouble_list: result, display_params: info } = response.data.result;

        const works: WorksList[] = result.map((data) => ({
          propertyId: data.property_id,
          propertyName: data.property_name,
          troubleId: data.trouble_id,
          title: data.title,
          priorityId: data.priority_id,
          priorityName: data.priority_name,
          troubleType: data.trouble_type,
          troubleTypeName: data.trouble_type_name,
          statusId: data.status_id,
          statusName: data.status_name,
          occurrenceDate: data.occurrence_date,
          occurrenceTime: data.occurrence_time,
          approvalDate: formatDateString(data.approval_datetime),
          description: data.description,
          picUserId: data.pic_user_id,
          picUserName: data.pic_user_name,
          isDeletedUser: data.is_deleted_user,
          latestComment: data.latest_comment,
          updateUserName: data.update_user_name,
          updateDateTime: data.update_datetime,
          dueDate: data.due_date,
          customPropertyList: data.custom_property_list,
        }));

        const worksListInfo: WorksListInfo = {
          totalHits: info.total,
          totalPages: info.total_page,
        };

        dispatch(actions.setWorks(works));
        dispatch(actions.setWorksListInfo(worksListInfo));
      })
      .catch(() => {
        dispatch(actions.setWorks([]));
        dispatch(actions.setWorksListInfo(null));
      });

    dispatch(actions.setIsLoading(false));
  };

/**
 * @summary 案件一覧取得(全件)
 * @param {number | null} propertyId 建物ID
 * @param {APIGetWorksAllRequestParams} params 検索パラメータ(query)
 * @param {CustomPropertySearchFromData} customPropertySearchForm 検索パラメータ(body)
 * @returns {Promise<void>}
 */
export const getWorksAllAsync =
  (
    propertyId: number | null,
    params: APIGetWorksAllRequestParams,
    customPropertySearchForm?: CustomPropertySearchFromData,
  ) =>
  async (dispatch: any) => {
    dispatch(actions.setIsLoading(true));

    const reqParams: APIGetWorksAllRequestParams = {
      ...params,
    };

    if (propertyId != null) reqParams.property_id = propertyId;

    await axios
      .post<APIGetWorksResponse>(
        '/troubles/list/all',
        { ...customPropertySearchForm },
        { params: reqParams },
      )
      .then((response) => {
        const { trouble_list: result } = response.data.result;

        const works: WorksList[] = result.map((data) => ({
          propertyId: data.property_id,
          propertyName: data.property_name,
          troubleId: data.trouble_id,
          title: data.title,
          priorityId: data.priority_id,
          priorityName: data.priority_name,
          troubleType: data.trouble_type,
          troubleTypeName: data.trouble_type_name,
          statusId: data.status_id,
          statusName: data.status_name,
          occurrenceDate: data.occurrence_date,
          occurrenceTime: data.occurrence_time,
          approvalDate: formatDateString(data.approval_datetime),
          description: data.description,
          picUserId: data.pic_user_id,
          picUserName: data.pic_user_name,
          isDeletedUser: data.is_deleted_user,
          latestComment: data.latest_comment,
          updateUserName: data.update_user_name,
          updateDateTime: data.update_datetime,
          dueDate: data.due_date,
          customPropertyList: data.custom_property_list,
        }));

        dispatch(actions.setWorksAll(works));
      })
      .catch(() => {
        dispatch(actions.setWorksAll([]));
      });

    dispatch(actions.setIsLoading(false));
  };

export const getWorksItemDataAsync = (propertyId: number | null) => async (dispatch: any) => {
  dispatch(actions.setIsLoading2(true));

  await axios
    .get<APIGetWorksItemDataResponse>(`/troubles/items`, {
      params: { property_id: propertyId },
    })
    .then((response) => {
      const result = response.data.result;

      const worksItemData: WorksItemData = {
        troubleTypeList: result.trouble_type_list.map((data) => ({
          troubleTypeId: data.trouble_type_id,
          troubleTypeName: data.trouble_type_name,
        })),
        troublePriorityList: result.trouble_priority_list.map((data) => ({
          troublePriorityId: data.trouble_priority_id,
          troublePriorityName: data.trouble_priority_name,
        })),
        troubleStatusList: result.trouble_status_list.map((data) => ({
          troubleStatusId: data.trouble_status_id,
          troubleStatusName: data.trouble_status_name,
        })),
        diagramList:
          result.diagram_list?.map((data) => ({
            diagramId: data.diagram_id,
            diagramName: data.diagram_name,
          })) ?? [],
        equipmentList:
          result.equipment_list?.map((data) => ({
            equipmentId: data.equipment_id,
            equipmentName: data.equipment_name,
            diagramId: data.diagram_id,
            floorId: data.floor_id,
            roomId: data.room_id,
          })) ?? [],
        floorList:
          result.floor_list?.map((data) => ({
            floorId: data.floor_id,
            floorName: data.floor_name,
            roomList: data.room_list.map((room) => ({
              roomId: room.room_id,
              roomName: room.room_name,
            })),
          })) ?? [],
        userList: result.user_list.map((data) => ({
          userId: data.user_id,
          userName: `${data.last_name} ${data.first_name}`,
        })),
      };

      dispatch(actions.setWorksItemData(worksItemData));
    })
    .catch(() => {
      dispatch(actions.setWorksItemData(null));
    });

  dispatch(actions.setIsLoading2(false));
};

/**
 * @summary 案件詳細取得
 * @param {number} workId 案件ID
 * @returns {Promise<void>}
 */
export const getWorkAsync = (workId: number) => async (dispatch: any) => {
  dispatch(actions.setIsLoading(true));

  await axios
    .get<APIGetWorkResponse>(`/troubles/${workId}`)
    .then((response) => {
      const v = response.data.result;

      const work: Work = {
        troubleId: v.trouble_id,
        title: v.title,
        matterId: v.matter_id,
        troubleType: v.trouble_type,
        troubleTypeName: v.trouble_type_name,
        dueDate: v.due_date,
        priorityId: v.priority_id,
        priorityName: v.priority_name,
        statusId: v.status_id,
        statusName: v.status_name,
        propertyId: v.property_id,
        propertyName: v.property_name,
        floorId: v.floor_id,
        floorName: v.floor_name,
        roomId: v.room_id,
        roomName: v.room_name,
        diagramId: v.diagram_id,
        diagramName: v.diagram_name,
        equipmentId: v.equipment_id,
        equipmentName: v.equipment_name,
        isDeletedEquipment: v.is_deleted_equipment,
        occurrenceDate: v.occurrence_date,
        occurrenceTime: v.occurrence_time,
        approvalDate: v.approval_datetime,
        picUserId: v.pic_user_id,
        picUserName: v.pic_user_name,
        isDeletedUser: v.is_deleted_user,
        updateDateTime: v.update_datetime,
        updateUserName: v.update_user_name,
        description: v.description,
        imageList: v.image_list.map((i) => ({
          fileId: i.file_id,
          fileName: i.file_name,
          fileSize: i.file_size,
          fileUrl: i.file_url,
          isMovie: i.is_movie,
          memo: i.memo,
          thumbnailContent: i.thumbnail_content,
          thumnailUrl: i.thumnail_url,
        })),
        fileList: v.file_list.map((f) => ({
          fileName: f.file_name,
          fileUrl: f.file_url,
          updateDatetime: f.update_datetime,
          updateUserName: f.update_user_name,
          fileSize: f.file_size,
        })),
        inspectionChecklistId: v.inspection_checklist_id,
        inspectionResultId: v.inspection_result_id,
        componentId: v.component_id,
        componentName: v.component_name,
        customPropertyList: v.custom_property_list,
      };

      dispatch(actions.setWork(work));
    })
    .catch(() => {
      dispatch(actions.setWork(null));
    });

  dispatch(actions.setIsLoading(false));
};

/**
 * @summary パラメータを元に案件 - 対応履歴のリストを取得
 * @param {number} propertyId 建物ID
 * @param {number} workId 案件ID
 * @param {number} page ページ数
 * @returns {void}
 */
export const getWorkHistoryAsync =
  (propertyId: number, workId: number, page: number) => async (dispatch: any) => {
    dispatch(actions.setIsLoading3(true));
    const reqParams: APIGetWorkHistoryRequestParams = { page };

    await axios
      .get<APIGetWorkHistoryResponse>(
        `/properties/${propertyId}/equipments/troubles/${workId}/trouble_shooting`,
        { params: reqParams },
      )
      .then((response) => {
        const { trouble_shooting_list: result, display_params: workHistoryDisplayParams } =
          response.data.result;

        const workHistory: WorkHistory[] = result.map((data) => ({
          troubleShootingId: data.trouble_shooting_id,
          troubleTypeId: data.trouble_type_id,
          troubleTypeName: data.trouble_type_name,
          iconId: data.icon_id,
          iconName: data.icon_name,
          responseUserId: data.response_user_id,
          responseUserName: data.response_user_name,
          isDeletedResponseUser: data.is_deleted_response_user,
          beforeStatus: data.before_status,
          beforeStatusName: data.before_status_name,
          afterStatus: data.after_status,
          afterStatusName: data.after_status_name,
          responseStartDate: data.response_start_date,
          responseStartTime: data.response_start_time,
          responseEndDate: data.response_end_date,
          responseEndTime: data.response_end_time,
          responseDetail: data.response_detail,
          updateUserId: data.update_user_id,
          updateUserName: data.update_user_name,
          isDeletedUpdateUser: data.is_deleted_update_user,
          isOutputToReport: data.is_output_to_report,
          createDatetime: data.create_datetime,
          updateDatetime: data.update_datetime,
          fileList: data.file_list.map((f) => ({
            fileName: f.file_name,
            fileUrl: f.file_url,
            updateDatetime: f.update_datetime,
            updateUserName: f.update_user_name,
            fileSize: f.file_size,
          })),
          imageList: data.image_list.map((i) => ({
            fileId: i.file_id,
            fileName: i.file_name,
            fileSize: i.file_size,
            fileUrl: i.file_url,
            isMovie: i.is_movie,
            memo: i.memo,
            thumbnailContent: i.thumbnail_content,
            thumnailUrl: i.thumnail_url,
          })),
        }));

        dispatch(actions.setWorkHistory(workHistory));
        dispatch(actions.setWorkHistoryDisplayParams(workHistoryDisplayParams));
      })
      .catch(() => {
        dispatch(actions.setWorkHistory([]));
        dispatch(actions.setWorkHistoryDisplayParams(null));
      });

    dispatch(actions.setIsLoading3(false));
  };

// 新規登録（作業登録）
export const createWorkAsync =
  (propertyId: number, data: WorkCreateSubmitData) => async (dispatch: any) => {
    dispatch(actions.setIsSubmitting(true));

    const reqParams: APICreateWorkRequestParams = {
      property_id: propertyId,
    };

    const work: APICreateWork = {
      title: data.title,
      trouble_type: data.troubleType,
      priority_id: data.priorityId,
      status_id: data.statusId,
      due_date: data.dueDate !== '' ? data.dueDate : null,
      diagram_id: data.diagramId,
      equipment_id: data.equipmentId,
      floor_id: data.floorId,
      room_id: data.roomId,
      occurrence_date: data.occurrenceDate,
      occurrence_time: data.occurrenceTime,
      pic_user_id: data.picUserId,
      description: data.description,
      image_list: data.imageList.map((i) => ({
        file_name: i.fileName,
        file_content: i.fileContent,
        memo: i.memo,
      })),
      file_list: data.fileList.map((f) => ({
        file_name: f.fileName,
        file_content: f.fileContent,
      })),
      custom_property_list: data.custom_property_list,
    };

    const requestData: APICreateWorkRequest = work;

    await axios
      .post<APICreateWorkResponse>(`/troubles`, requestData, { params: reqParams })
      .then((response) => {
        const workId = response.data.result.trouble_id;
        dispatch(actions.setIsNavigating(true));
        dispatch(actions.setIsNavigatingTo(workId));
        dispatch(actions.refresh());
      })
      .catch(() => {});

    dispatch(actions.setIsSubmitting(false));
  };

export const updateWorkAsync =
  (propertyId: number, workId: number, data: WorkUpdateSubmitDate) => async (dispatch: any) => {
    dispatch(actions.setIsSubmitting(true));

    const reqParams: APIUpdateWorkRequestParams = {
      property_id: propertyId,
    };

    const work: APIUpdateWork = {
      status_id: data.status_id,
      title: data.title,
      trouble_type: data.trouble_type,
      priority_id: data.priority_id,
      due_date: data.due_date,
      diagram_id: data.diagram_id,
      equipment_id: data.equipment_id,
      floor_id: data.floor_id,
      room_id: data.room_id,
      occurrence_date: data.occurrence_date,
      occurrence_time: data.occurrence_time,
      pic_user_id: data.pic_user_id,
      description: data.description,
      image_list: data.image_list.map((i) => ({
        file_name: i.file_name,
        file_content: i.file_content,
        is_delete_file: i.is_delete_file,
        memo: i.memo,
      })),
      file_list: data.file_list.map((f) => ({
        file_name: f.file_name,
        file_content: f.file_content,
        is_delete_file: f.is_delete_file,
      })),
      custom_property_list: data.custom_property_list,
    };

    const requestData: APIUpdateWorkRequest = work;

    await axios
      .put(`/troubles/${workId}`, requestData, { params: reqParams })
      .then(() => {
        dispatch(actions.setIsNavigating(true));
        dispatch(actions.refresh());
      })
      .catch(() => {});

    dispatch(actions.setIsSubmitting(false));
  };

export const deleteWorksAsync = (workIds: number[]) => async (dispatch: any) => {
  dispatch(actions.setIsSubmitting(true));

  const requestData: APIDeleteWorksRequest = {
    trouble_list: workIds.map((v) => ({
      trouble_id: v,
    })),
  };

  await axios
    .post(`/troubles/delete`, requestData)
    .then(() => {
      dispatch(actions.setIsNavigating(true));
      dispatch(actions.setIsNavigatingTo(-1));
    })
    .catch(() => {});

  dispatch(actions.setIsSubmitting(false));
};

/**
 * @summary 案件対応履歴、コメントを登録する
 * @param {number} propertyId 建物ID
 * @param {number} workId 案件ID
 * @param {WorkHistorySubmitData} data
 * @returns {Promise<boolean>} 登録成否
 */
export const createWorkHistoryAsync =
  (propertyId: number, workId: number, data: WorkHistorySubmitData) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    const reqParams: APICreateWorkHistoryRequestParams = {
      property_id: propertyId,
    };

    const work: APICreateWorkHistory = {
      after_status: data.nextStatus,
      response_detail: data.responseDetail,
      response_end_date: data.responseEndDate,
      response_end_time: data.responseEndTime,
      response_start_date: data.responseStartDate,
      response_start_time: data.responseStartTime,
      response_user_id: data.responseUserId,
      trouble_type_id: data.troubleShootingTypeId,
      is_output_to_report: data.isOutputToReport,
      file_list: data.fileList.map((f) => ({
        file_name: f.fileName,
        file_content: f.fileContent,
      })),
      image_list: data.imageList.map((i) => ({
        file_name: i.fileName,
        file_content: i.fileContent,
        memo: i.memo,
      })),
    };

    const requestData: APICreateWorkHistoryRequest = work;

    return await axios
      .post(
        `/properties/${propertyId}/equipments/troubles/${workId}/trouble_shooting`,
        requestData,
        { params: reqParams },
      )
      .then(() => {
        dispatch(actions.refresh());
        dispatch(actions.refresh3());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 案件対応履歴、コメントを編集する
 * @param {number} propertyId 建物ID
 * @param {number} workId 案件ID
 * @param {number} historyId 履歴ID
 * @param {WorkHistorySubmitData} data
 * @returns {Promise<boolean>} 登録成否
 */
export const updateWorkHistoryAsync =
  (propertyId: number, workId: number, historyId: number, data: WorkHistorySubmitData) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    const reqParams: APIUpdateWorkHistoryRequestParams = {
      property_id: propertyId,
    };

    const work: APIUpdateWorkHistory = {
      after_status: data.nextStatus,
      response_detail: data.responseDetail,
      response_end_date: data.responseEndDate,
      response_end_time: data.responseEndTime,
      response_start_date: data.responseStartDate,
      response_start_time: data.responseStartTime,
      response_user_id: data.responseUserId,
      trouble_type_id: data.troubleShootingTypeId,
      is_output_to_report: data.isOutputToReport,
      file_list: data.fileList.map((f) => ({
        file_name: f.fileName,
        file_content: f.fileContent,
        is_delete_file: f.deleteFlag,
      })),
      image_list: data.imageList.map((i) => ({
        file_name: i.fileName,
        file_content: i.fileContent,
        memo: i.memo,
        is_delete_file: i.deleteFlag,
      })),
    };

    const requestData: APIUpdateWorkHistoryRequest = work;

    return await axios
      .put(
        `/properties/${propertyId}/equipments/troubles/${workId}/trouble_shooting/${historyId}`,
        requestData,
        { params: reqParams },
      )
      .then(() => {
        dispatch(actions.refresh());
        dispatch(actions.refresh3());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 案件承認・差し戻し
 * @param {number} workId 案件ID
 * @param {APIApproveWorkHistoryRequest} approveRequest
 * @returns {Promise<boolean>} 登録成否
 */
export const approveWorkAsync =
  (workId: number, approveRequest: APIApproveWorkHistoryRequest) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .patch(`/troubles/${workId}/approve`, approveRequest)
      .then(() => {
        dispatch(actions.refresh());
        dispatch(actions.refresh3());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

/**
 * @summary 案件取り戻し
 * @param {number} workId 案件ID
 * @param {APIApproveWorkHistoryRequest} approveRequest
 * @returns {Promise<boolean>} 登録成否
 */
export const takeBackWorkAsync =
  (workId: number, approveRequest: APITakeBackWorkHistoryRequest) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .patch(`/troubles/${workId}/uncompleted`, approveRequest)
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

export const exportWorkReportAsync =
  (
    workId: number,
    needArchive = false,
  ): ((dispatch: any) => Promise<{ file_name: string; file_content: string } | null>) =>
  async (dispatch: any) => {
    dispatch(commonActions.setIsProcessing(true));
    const result = await axios
      .get<APIExportWorkReportResponse>(
        `/troubles/${workId}/export${needArchive ? '?need_archive=true' : ''}`,
      )
      .then((response) => {
        return response.data.result;
      })
      .catch(() => {
        return null;
      })
      .finally(() => {
        dispatch(commonActions.setIsProcessing(false));
      });

    return result;
  };

export const exportWorksListAsync =
  (reqParams: number[], customProperty?: any) => async (dispatch: any) => {
    dispatch(commonActions.setIsProcessing(true));

    const result = await axios
      .post<APIExportCsvFormatResponse>(`/troubles/export`, {
        target_records: reqParams,
        custom_property: customProperty,
      })
      .then((response) => {
        return {
          fileName: response.data.result.file_name,
          fileContent: response.data.result.file_content,
        };
      })
      .catch(() => {
        return null;
      })
      .finally(() => {
        dispatch(commonActions.setIsProcessing(false));
      });

    return result;
  };

/** 不具合連絡事項取得 */
export const getWorkInformationAsync = (workId: number) => async (dispatch: any) => {
  dispatch(actions.setIsLoading3(true));

  await axios
    .get<APIGetWorkInformationResponse>(`/troubles/${workId}/information`)
    .then((response) => {
      const resultData = response.data.result;
      const workInfoData: WorkInfo = {
        troubleId: resultData.trouble_id,
        title: resultData.title,
        propertyId: resultData.property_id,
        propertyName: resultData.property_name,
        message: resultData.message,
        files: (resultData.file_list ?? []).map((f) => ({
          fileId: f.file_id,
          fileName: f.file_name,
          fileUrl: f.file_url,
          fileSize: f.file_size,
        })),
      };
      dispatch(actions.setWorkInfo(workInfoData));
    })
    .catch(() => {
      dispatch(actions.setWorkInfo(null));
    });
  dispatch(actions.setIsLoading3(false));
};

/** 案件報告書フォーマットプレースホルダー取得 */
export const getTroubleReportPlaceHoldersAsync = (propertyId: number) => async (dispatch: any) => {
  dispatch(actions.setIsLoading(true));

  await axios
    .get<APIGetTroubleReportPlaceHoldersResponse>(
      `/properties/${propertyId}/troubles/format/placeholders`,
    )
    .then((response) => {
      const result: APIGetTroubleReportPlaceHoldersResponse = response.data;
      dispatch(actions.setTroubleReportPlaceholders(new TroubleReportPlaceholderRelation(result)));
    })
    .catch(() => {
      dispatch(actions.setTroubleReportPlaceholders(new TroubleReportPlaceholderRelation(null)));
    });

  dispatch(actions.setIsLoading(false));
};

export const downloadWorkReportFormatAsync =
  (
    propertyId: number,
  ): ((dispatch: any) => Promise<{ fileName: string; fileContent: string } | null>) =>
  async (dispatch: any) => {
    dispatch(commonActions.setIsProcessing(true));
    const result = await axios
      .get<APIDownloadTroubleReportFormatResponse>(
        `/properties/${propertyId}/troubles/format/download`,
      )
      .then((response) => {
        return {
          fileName: response.data.result.file_name,
          fileContent: response.data.result.file_content,
        };
      })
      .catch(() => {
        return null;
      })
      .finally(() => {
        dispatch(commonActions.setIsProcessing(false));
      });

    return result;
  };

/**
 * @summary 案件報告書フォーマットを登録する
 * @param {number} propertyId 建物ID
 * @param {formData} formData フォーマットファイル
 * @returns {Promise<boolean>} 登録成否
 */
export const uploadWorkReportFormatAsync =
  (propertyId: number, formData: FormData) =>
  async (dispatch: any): Promise<boolean> => {
    dispatch(actions.setIsSubmitting(true));

    return await axios
      .put(`/properties/${propertyId}/troubles/format`, formData, {
        headers: { 'content-type': 'multipart/form-data' },
      })
      .then(() => {
        dispatch(actions.refresh());
        return true;
      })
      .catch(() => {
        return false;
      })
      .finally(() => {
        dispatch(actions.setIsSubmitting(false));
      });
  };

export const deleteTroubleReportFormatAsync = (propertyId: number) => async (dispatch: any) => {
  dispatch(actions.setIsSubmitting(true));

  await axios
    .delete(`/properties/${propertyId}/troubles/format`)
    .then(() => {
      dispatch(actions.refresh());
    })
    .catch(() => {});

  dispatch(actions.setIsSubmitting(false));
};
